import React, { useState, useRef, useCallback, useEffect } from 'react';
import VideoPlayer from './VideoPlayer';
import ClickTrackOverlay from './ClickTrackOverlay';
import TimelineScrubber from './TimelineScrubber';
import ErrorDisplay from './ErrorDisplay';
import PerformanceMetrics from './PerformanceMetrics';
import PerformanceMetricsCard from './PerformanceMetricsCard';
import { useD4RTData } from '../../hooks/useD4RTData';
import { Film, X } from 'lucide-react';

/**
 * D4DQueryInterface - Main 4D navigation container
 *
 * Orchestrates:
 * - Click-to-track with instant feedback
 * - Timeline scrubbing with debouncing
 * - Error display with numeric codes
 * - Performance metrics
 *
 * Sport-agnostic design - works with any sport
 */

// Security: Safe error messages (prevents XSS from backend)
const ERROR_CODE_MESSAGES = {
  1000: "Failed to load video file. Please check the video path.",
  1001: "The video file couldn't be opened. Try a different format.",
  1100: "Video format not supported. Use MP4 or MOV.",
  1200: "Failed to extract trajectory. Video may be too dark or blurry.",
  1300: "Failed to extract skeletal data. Person not clearly visible.",
  1400: "Gemini API rate limit exceeded. Please wait a moment.",
  9999: "Internal server error. Please try again."
};

const DEFAULT_VIDEO_WIDTH = 1920;
const DEFAULT_VIDEO_HEIGHT = 1080;

function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function extractUV(pointLike) {
  if (!pointLike) return null;

  // FastAPI tuple-like format: [frame_index, {u, v}]
  const source = Array.isArray(pointLike) ? (pointLike[1] || pointLike[0]) : pointLike;
  if (!source || typeof source !== 'object') return null;

  if (source.position_2d) {
    return {
      u: Number(source.position_2d.u),
      v: Number(source.position_2d.v),
      confidence: Number(source.confidence ?? source.position_2d.confidence ?? 0.5),
    };
  }

  if (source.point) {
    return {
      u: Number(source.point.u),
      v: Number(source.point.v),
      confidence: Number(source.confidence ?? source.point.confidence ?? 0.5),
    };
  }

  return {
    u: Number(source.u),
    v: Number(source.v),
    confidence: Number(source.confidence ?? 0.5),
  };
}

function normalizeTrajectoryForOverlay(trajectory, videoWidth, videoHeight) {
  if (!Array.isArray(trajectory)) return [];

  const width = videoWidth > 0 ? videoWidth : DEFAULT_VIDEO_WIDTH;
  const height = videoHeight > 0 ? videoHeight : DEFAULT_VIDEO_HEIGHT;

  return trajectory
    .map((pointLike) => {
      const extracted = extractUV(pointLike);
      if (!extracted || !Number.isFinite(extracted.u) || !Number.isFinite(extracted.v)) {
        return null;
      }

      const isNormalized = extracted.u >= 0 && extracted.u <= 1 && extracted.v >= 0 && extracted.v <= 1;
      return {
        u: isNormalized ? clamp01(extracted.u) : clamp01(extracted.u / width),
        v: isNormalized ? clamp01(extracted.v) : clamp01(extracted.v / height),
        confidence: Number.isFinite(extracted.confidence) ? extracted.confidence : 0.5,
      };
    })
    .filter(Boolean);
}

const D4DQueryInterface = ({ videoId, onClose }) => {
  // VideoPlayer state (required props for VideoPlayer component)
  const [videoState, setVideoState] = useState({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
  });

  // State
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(0);
  const [trajectories, setTrajectories] = useState([]);
  const [skeletalData, setSkeletalData] = useState(null);
  const [trackedFrames, setTrackedFrames] = useState([]);
  const [clickMarkers, setClickMarkers] = useState([]);
  const [error, setError] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({});

  // Sync currentFrame from videoState.currentTime
  useEffect(() => {
    setCurrentFrame(Math.floor(videoState.currentTime * 30)); // 30fps assumption
  }, [videoState.currentTime]);

  // Update totalFrames from video duration
  useEffect(() => {
    if (videoState.duration > 0) {
      setTotalFrames(Math.floor(videoState.duration * 30));
    }
  }, [videoState.duration]);

  // API hook
  const {
    getActionTrajectory,
    getSkeletalTrack,
    isLoading,
    error: apiError,
  } = useD4RTData();

  // Refs - container div ref for ClickTrackOverlay (VideoPlayer is NOT forwardRef)
  const videoContainerRef = useRef(null);

  /**
   * Handle video click for point tracking
   * Shows immediate feedback, then calls API
   */
  const handleVideoClick = useCallback(async (clickPoint, e) => {
    const startTime = performance.now();
    const markerId = Date.now();

    try {
      const videoEl = videoContainerRef.current?.querySelector('video');
      const videoWidth = videoEl?.videoWidth || DEFAULT_VIDEO_WIDTH;
      const videoHeight = videoEl?.videoHeight || DEFAULT_VIDEO_HEIGHT;
      const apiPoint = {
        u: clickPoint.u * videoWidth,
        v: clickPoint.v * videoHeight,
      };

      // IMMEDIATE visual feedback (< 16ms)
      setClickMarkers(prev => [...prev, {
        id: markerId,
        point: clickPoint,
        status: 'analyzing'
      }]);

      // API call (3-20 seconds)
      const result = await getActionTrajectory(
        videoId,
        currentFrame,
        currentFrame + 60, // 2 seconds @ 30fps
        apiPoint
      );
      const normalizedPoints = normalizeTrajectoryForOverlay(result.trajectory || [], videoWidth, videoHeight);

      // Update marker to success
      setClickMarkers(prev => prev.map(m =>
        m.id === markerId
          ? { ...m, status: 'success', trajectory: normalizedPoints }
          : m
      ));

      // Add trajectory to display
      setTrajectories(prev => [...prev, {
        id: markerId,
        points: normalizedPoints,
        sport: result.sport,
        ...result
      }]);

      // Track this frame
      setTrackedFrames(prev => [...prev, currentFrame]);

      // Update performance metrics
      const duration = performance.now() - startTime;
      setPerformanceMetrics(prev => ({
        ...prev,
        lastTrackTime: duration,
      }));

    } catch (err) {
      // Security: Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('[D4DQueryInterface] Track failed:', err);
      }

      // Extract numeric error code from the response.
      // FastAPI wraps errors in {detail: {error_code, ...}} but some errors
      // skip the detail wrapper, so we check both paths. Falls back to 9999
      // (generic internal error) if neither path yields a code.
      const errorCode = err.response?.data?.detail?.error_code || err.response?.data?.error_code || 9999;

      // Update marker to error
      setClickMarkers(prev => prev.map(m =>
        m.id === markerId
          ? {
              ...m,
              status: 'error',
              errorCode: errorCode
            }
          : m
      ));

      // Security: Use safe error message from code map (prevents XSS)
      setError({
        error_code: errorCode,
        error_message: ERROR_CODE_MESSAGES[errorCode] || "An unexpected error occurred",
        context: err.response?.data?.detail?.context || err.response?.data?.context || {},
      });
    }
  }, [videoId, currentFrame, getActionTrajectory]);

  /**
   * Handle frame change from timeline scrubbing
   * Debounced in TimelineScrubber component
   */
  const handleFrameChange = useCallback(async (frameIndex) => {
    const startTime = performance.now();

    setCurrentFrame(frameIndex);

    try {
      // Load skeletal data for this frame (with buffer)
      const result = await getSkeletalTrack(
        videoId,
        Math.max(0, frameIndex - 5),
        frameIndex + 5,
        null // All joints
      );

      setSkeletalData(result);

      const duration = performance.now() - startTime;
      setPerformanceMetrics(prev => ({
        ...prev,
        lastSkeletalLoadTime: duration,
      }));

    } catch (err) {
      // Security: Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('[D4DQueryInterface] Skeletal load failed:', err);
      }
      // Don't show error for skeletal loads (non-critical)
    }
  }, [videoId, getSkeletalTrack]);

  /**
   * Clear all tracked trajectories
   */
  const handleClearTracks = useCallback(() => {
    setTrajectories([]);
    setTrackedFrames([]);
    setClickMarkers([]);
  }, []);

  /**
   * Retry last action (for error recovery)
   */
  const handleRetry = useCallback(() => {
    setError(null);
    // Could re-trigger last action here if needed
  }, []);

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-zinc-900 border-b border-cyan-500/30 flex items-center justify-between px-6 z-50">
        <div className="flex items-center gap-3">
          <Film className="text-cyan-400" size={24} />
          <h2 className="text-xl font-bold text-white">4D Navigation Interface</h2>
          {isLoading && (
            <span className="text-sm text-cyan-400 animate-pulse">Loading...</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <PerformanceMetrics metrics={performanceMetrics} />
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors"
            aria-label="Close 4D navigation"
          >
            <X className="text-cyan-400" size={20} />
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="absolute top-16 bottom-24 left-0 right-0 p-4 overflow-auto">
        {/* Error display */}
        {error && (
          <ErrorDisplay
            error={error}
            onDismiss={() => setError(null)}
            onRetry={handleRetry}
          />
        )}

        {/* Video with click overlay */}
        <div ref={videoContainerRef} className="relative bg-zinc-900 border border-cyan-500/30 rounded-lg overflow-hidden max-w-5xl mx-auto">
          <VideoPlayer
            videoUrl={videoId}
            analysisData={null}
            videoState={videoState}
            setVideoState={setVideoState}
          />
          <ClickTrackOverlay
            trajectories={trajectories}
            clickMarkers={clickMarkers}
            onVideoClick={handleVideoClick}
            videoRef={videoContainerRef}
          />
        </div>

        {/* Performance data - shown when trajectory exists */}
        {trajectories.length > 0 && trajectories[trajectories.length - 1] && (
          <div className="max-w-5xl mx-auto mt-4">
            <PerformanceMetricsCard trajectoryData={trajectories[trajectories.length - 1]} />
          </div>
        )}

        {/* Controls panel */}
        <div className="max-w-5xl mx-auto mt-4 bg-zinc-900 border border-cyan-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <span className="text-zinc-400 text-sm">
                Tracked Points: <span className="text-cyan-400 font-semibold">{trajectories.length}</span>
              </span>
              <span className="text-zinc-400 text-sm">
                Frame: <span className="text-cyan-400 font-mono">{currentFrame} / {totalFrames}</span>
              </span>
            </div>

            <button
              onClick={handleClearTracks}
              disabled={trajectories.length === 0}
              className="px-4 py-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed text-cyan-400 transition-colors text-sm"
            >
              Clear Tracks
            </button>
          </div>

          {/* Instructions */}
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 mb-3">
            <p className="text-zinc-400 text-sm">
              <span className="text-cyan-400 font-semibold">Click</span> anywhere on the video to track that point through time.
              The system will automatically detect the sport and show appropriate physics data.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom: Timeline scrubber */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-zinc-900 border-t border-cyan-500/30 p-4 z-50">
        <TimelineScrubber
          currentFrame={currentFrame}
          totalFrames={totalFrames}
          onFrameChange={handleFrameChange}
          trackedFrames={trackedFrames}
          fps={30}
        />
      </div>
    </div>
  );
};

export default D4DQueryInterface;
